<template>
    <VJoyInputNumber
        v-if="unit !== undefined"
        ref="vJoyInputRef"
        autocomplete="off"
        :model-value="maskedValue"
        v-bind="reactive(inputProps)"
        :placeholder="computedPlaceholder"
        :data-testid="dataTestid"
        :unit="unit"
        type="text"
        @update:model-value="handleNonSourceUpdate"
    >
        <slot />
    </VJoyInputNumber>
    <VJoyInput
        v-else
        ref="vJoyInputRef"
        autocomplete="off"
        :model-value="maskedValue"
        v-bind="reactive(inputProps)"
        :placeholder="computedPlaceholder"
        :data-testid="dataTestid"
        type="text"
        @update:model-value="handleNonSourceUpdate"
    >
        <slot />
    </VJoyInput>
</template>

<script
    setup
    lang="ts"
    generic="
        TPreset extends MaltInputMaskPreset,
        ModelValue extends string | number | undefined = (TPreset extends 'price' | 'integer' ? number : string) | undefined
    "
>
    import {computed, nextTick, onMounted, onUnmounted, reactive, ref, shallowRef, toRefs, useTemplateRef, watch, type WatchHandle} from 'vue';
    import {type FactoryArg, type FactoryOpts, type InputMask} from 'imask';
    import IMask from 'imask/holder';
    import {useLocale, useLogger} from '#imports';
    import {type VJoyInputProps, VJoyInput, VJoyInputNumber} from '@maltjoy/core-vue';
    import {useDatePatterns} from '../common';
    import {useMask} from './composables';

    export type MaltInputMaskPreset = 'date' | 'date-range' | 'date-month-year' | 'date-year' | 'date-time' | 'integer' | 'price';

    export interface MaltInputMaskProps<TPreset extends MaltInputMaskPreset = MaltInputMaskPreset> extends VJoyInputProps {
        /**
         * Imaskjs options https://imask.js.org/
         *
         * Recommanded settings:
         * - min: Minimum value for the input
         * - max: Maximum value for the input
         * */
        maskOptions?: Partial<FactoryOpts>;
        /**
         * Preconfigured mask configurations
         * - date: Classic date format (`dd/MM/yyyy`).
         * - date-range: Date range format (`dd/MM/yyyy - dd/MM/yyyy`).
         * - date-month-year: Date month year format (`MM/yyyy`).
         * - date-year: Date year format (`yyyy`).
         * - date-time: Date time format (`dd/MM/yyyy HH:mm`).
         * - integer: Only integers, thousand separator (ex: 1000 -> 1 000)
         * - price: Correct decimal point for price input (ex: 10.00 -> 10,00), maximum 2 decimals, thousands separator
         * */
        preset?: TPreset;
        placeholder?: string;
        dataTestid?: string;
        unit?: string;
    }

    const props = withDefaults(defineProps<MaltInputMaskProps<TPreset>>(), {
        maskOptions: () => ({}),
    });

    /** Processed model value without formatting */
    const modelValue = defineModel<ModelValue | undefined>();

    const {maskOptions, preset, modelValue: _oldModel, placeholder, unit, ...inputProps} = toRefs(props);

    /** IMask factory object */
    const mask = shallowRef<InputMask<FactoryArg>>();

    /** Displayed value with formatting */
    const maskedValue = ref<string>('');

    const vJoyInputRef = useTemplateRef('vJoyInputRef');
    const joyInputElementRef = ref<HTMLInputElement>();

    const {languageCode} = useLocale();
    const logger = useLogger();

    const {datePlaceholderPattern, dateMonthYearPlaceholderPattern, dateTimePlaceholderPattern, yearOnlyPlaceholderPattern} = useDatePatterns();

    const {resolvedMaskOptions} = useMask({preset, maskOptions});

    const computedPlaceholder = computed(() => {
        if (placeholder.value) {
            return placeholder.value;
        } else if (preset.value === 'date') {
            return datePlaceholderPattern;
        } else if (preset.value === 'date-range') {
            return `${datePlaceholderPattern} - ${datePlaceholderPattern}`;
        } else if (preset.value === 'date-month-year') {
            return dateMonthYearPlaceholderPattern;
        } else if (preset.value === 'date-year') {
            return yearOnlyPlaceholderPattern;
        } else if (preset.value === 'date-time') {
            return dateTimePlaceholderPattern;
        } else if (preset.value === 'integer') {
            return '0';
        } else if (preset.value === 'price') {
            // Display locale aware placeholder (0,00, or 0.00)
            return new Intl.NumberFormat(languageCode.value, {minimumFractionDigits: 2, maximumFractionDigits: 2}).format(0);
        }
        return undefined;
    });

    const isDateRelatedInput = computed(() => {
        return preset.value && ['date', 'date-range', 'date-month-year', 'date-year', 'date-time'].includes(preset.value);
    });

    const isNumberRelatedInput = computed(() => {
        return preset.value && ['integer', 'price'].includes(preset.value);
    });

    function initializeInput() {
        joyInputElementRef.value = vJoyInputRef.value?.$el?.querySelector('input') as HTMLInputElement | undefined;
        if (!joyInputElementRef.value) {
            logger.warn('[MaltInputMask] No joy input element found');
            return;
        }
        if (resolvedMaskOptions.value) {
            mask.value = IMask(joyInputElementRef.value, resolvedMaskOptions.value);
            nextTick(() => {
                updateMaskValue(modelValue.value);
            });
            mask.value.updateValue();
            createIMaskListeners();
            createClipboardListeners();
        }
    }

    onMounted(initializeInput);

    watch(
        unit,
        async (currentUnit, previousUnit) => {
            if ((currentUnit === undefined) === (previousUnit === undefined)) return;

            destroyClipboardListeners();
            destroyIMaskListeners();
            mask.value?.destroy();
            mask.value = undefined;
            joyInputElementRef.value = undefined;

            await nextTick();
            initializeInput();
        },
        {flush: 'post'},
    );

    function handlePasteMaskedContent(event: ClipboardEvent) {
        event.preventDefault();

        const pastedText = event.clipboardData?.getData('text');
        if (!pastedText || !mask.value || !joyInputElementRef.value) return;

        const start = joyInputElementRef.value.selectionStart ?? 0;
        const end = joyInputElementRef.value.selectionEnd ?? 0;

        const currentValue = mask.value.unmaskedValue;
        const currentValueBeforeSelection = currentValue.slice(0, start);
        const currentValueAfterSelection = currentValue.slice(end);
        const newValue = `${currentValueBeforeSelection}${pastedText}${currentValueAfterSelection}`;

        updateMaskValue(newValue);
    }

    function handleCopyMaskedContent(event: ClipboardEvent) {
        if (!mask.value || !joyInputElementRef.value) return;

        const start = joyInputElementRef.value.selectionStart ?? 0;
        const end = joyInputElementRef.value.selectionEnd ?? 0;

        /** Only override if the whole value is selected */
        if (start !== end && start === 0 && end === maskedValue.value.length) {
            event.preventDefault();

            const valueToCopy = isDateRelatedInput.value ? mask.value.masked.value : mask.value.unmaskedValue;

            const selectedText = valueToCopy.slice(start, end);
            event.clipboardData?.setData('text/plain', selectedText);
        }
    }

    function createClipboardListeners() {
        if (!joyInputElementRef.value) return;

        joyInputElementRef.value.addEventListener('paste', handlePasteMaskedContent);
        joyInputElementRef.value.addEventListener('copy', handleCopyMaskedContent);
    }

    function destroyClipboardListeners() {
        if (!joyInputElementRef.value) return;

        joyInputElementRef.value.removeEventListener('paste', handlePasteMaskedContent);
        joyInputElementRef.value.removeEventListener('copy', handleCopyMaskedContent);
    }

    function createIMaskListeners() {
        if (!mask.value) return;

        mask.value.on('accept', async () => {
            $unwatchModelValue();
            if (!mask.value?.unmaskedValue) {
                modelValue.value = '' as ModelValue;
            }
            maskedValue.value = mask.value?.masked.value ?? '';
            await nextTick();
            defineWatchModelValue();
        });

        mask.value.on('complete', async () => {
            $unwatchModelValue();
            if (isDateRelatedInput.value) {
                // Keep the masked value for date related inputs
                modelValue.value = mask.value?.masked.value as ModelValue;
            } else if (isNumberRelatedInput.value) {
                const toNumberValue = parseFloat(mask.value?.unmaskedValue ?? '');
                if (toNumberValue && !isNaN(toNumberValue)) {
                    modelValue.value = toNumberValue as ModelValue;
                } else {
                    modelValue.value = mask.value?.unmaskedValue as ModelValue;
                }
            } else {
                modelValue.value = mask.value?.unmaskedValue as ModelValue;
            }
            maskedValue.value = mask.value?.masked.value ?? '';
            await nextTick();
            defineWatchModelValue();
        });
    }

    function destroyIMaskListeners() {
        if (!mask.value) return;
        // @ts-expect-error Wrong type definitions https://imask.js.org/guide.html
        mask.value?.off('accept');
        // @ts-expect-error Wrong type definitions https://imask.js.org/guide.html
        mask.value?.off('complete');
    }

    function defineWatchModelValue() {
        /** We need to update the Joy input value when the model value changes, but without retriggering the input event and doing an infinite loop */
        $unwatchModelValue = watch(modelValue, updateMaskValue);
    }

    async function updateMaskValue(newValue: string | number | undefined) {
        await nextTick();
        if (mask.value && joyInputElementRef.value) {
            destroyIMaskListeners();

            triggerVJoyInputUpdate(newValue);

            mask.value?.updateValue();
            if (!newValue) {
                maskedValue.value = '';
                mask.value?.updateValue();
            } else {
                maskedValue.value = mask.value?.masked.value ?? '';
            }
            createIMaskListeners();
        }
    }

    function triggerVJoyInputUpdate(value: string | number | undefined | null) {
        if (joyInputElementRef.value) {
            joyInputElementRef.value.value = typeof value === 'number' ? value.toString() : (value ?? '');
            joyInputElementRef.value.dispatchEvent(new Event('input'));
        }
    }

    /** This is not the main source of update, it's only used when field is cleared or if no mask option is provided */
    function handleNonSourceUpdate(value: string) {
        // Only allow clearing the value if the input is clearable
        if (!value) {
            modelValue.value = undefined;
        } else if (!resolvedMaskOptions.value) {
            modelValue.value = value as ModelValue;
        }
    }

    let $unwatchModelValue: WatchHandle;
    defineWatchModelValue();

    onUnmounted(() => {
        destroyClipboardListeners();
        mask.value?.destroy();
    });
</script>
